import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Footer from '../Layouts/Footer';
import logo from '../../assets/VisibeenLogo.png';

const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#F8F8F8',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column'
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: '#0B91D6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '80px',
  padding: '0 30px',
  [theme.breakpoints.down('md')]: {
    height: '64px',
    padding: '0 20px'
  },
  [theme.breakpoints.down('sm')]: {
    height: '56px',
    padding: '0 16px'
  }
}));

const HeaderLogo = styled('img')(({ theme }) => ({
  height: '40px',
  width: 'auto',
  cursor: 'pointer',
  transition: 'opacity 0.2s ease',
  '&:hover': {
    opacity: 0.8
  },
  [theme.breakpoints.down('md')]: {
    height: '32px'
  },
  [theme.breakpoints.down('sm')]: {
    height: '28px'
  }
}));

const ContentSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: '60px 0',
  [theme.breakpoints.down('md')]: {
    padding: '40px 0'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '32px 0'
  }
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  maxWidth: '900px',
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  padding: '48px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  [theme.breakpoints.down('md')]: {
    padding: '32px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '24px'
  }
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '32px',
  fontWeight: 600,
  color: '#121927',
  textAlign: 'center',
  marginBottom: '8px',
  [theme.breakpoints.down('md')]: {
    fontSize: '28px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '24px'
  }
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#6B7280',
  textAlign: 'center',
  marginBottom: '48px',
  [theme.breakpoints.down('sm')]: {
    marginBottom: '32px',
    fontSize: '13px'
  }
}));

const IntroText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#4B5563',
  lineHeight: '1.6',
  marginBottom: '32px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '18px',
  fontWeight: 600,
  color: '#121927',
  marginTop: '32px',
  marginBottom: '16px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    marginTop: '24px'
  }
}));

const SectionContent = styled(Box)(({ theme }) => ({
  marginBottom: '24px'
}));

const BulletPoint = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#4B5563',
  lineHeight: '1.6',
  marginBottom: '12px',
  paddingLeft: '20px',
  position: 'relative',
  '&:before': {
    content: '"•"',
    position: 'absolute',
    left: '0',
    color: '#0B91D6',
    fontWeight: 600
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px'
  }
}));

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <PageContainer>
      <HeaderSection>
        <HeaderLogo
          src={logo}
          alt="Visibeen"
          onClick={() => navigate('/dashboard')}
          title="Go to Dashboard"
        />
      </HeaderSection>

      <ContentSection>
        <ContentContainer>
          <PageTitle>Terms of service</PageTitle>
          <PageSubtitle>Visibeen – Google Business Profile & AI Usage Terms</PageSubtitle>

          <IntroText>
            Policy for Website at time of paid version Perfect! Here's a concise, website-friendly version of
            Visibeen's GBP and AI Usage Terms that's readable, clear, and legally protective. It's structured
            for display on your website's Terms page.
          </IntroText>

          <SectionTitle>Visibeen – Google Business Profile & AI Usage Terms</SectionTitle>

          <SectionTitle>1. Your Account & Access</SectionTitle>
          <SectionContent>
            <BulletPoint>
              By linking your Google Business Profile (GBP) to Visibeen, you grant us limited access to help manage, audit, and
              improve your profile.
            </BulletPoint>
            <BulletPoint>
              Keep your login credentials secure. Sharing your account may result in suspension.
            </BulletPoint>
          </SectionContent>

          <SectionTitle>2. How We Help</SectionTitle>
          <SectionContent>
            <BulletPoint>
              Visibeen provides tools to audit your GBP, calculate your Visibeen Score, suggest tasks, and compare with
              competitors.
            </BulletPoint>
            <BulletPoint>
              All recommendations are advisory only. You must review and approve all suggestions before taking action.
            </BulletPoint>
          </SectionContent>

          <SectionTitle>3. AI & Content Use</SectionTitle>
          <SectionContent>
            <BulletPoint>
              We use generative AI to suggest posts, replies, or optimization ideas. But terms or inaccuracies may occur.
            </BulletPoint>
            <BulletPoint>
              Since AI prompts are highly tested but since its machine model & Visibeen is not responsible for any outcomes resulting
              from AI-generated content, including posted content.
            </BulletPoint>
          </SectionContent>

          <SectionTitle>4. Your Responsibilities</SectionTitle>
          <SectionContent>
            <BulletPoint>
              Ensure all AI suggestions and actions comply with Google policies and applicable laws.
            </BulletPoint>
            <BulletPoint>
              Maintain accurate business information in your profile.
            </BulletPoint>
            <BulletPoint>
              Verify all content before posting.
            </BulletPoint>
          </SectionContent>

          <SectionTitle>5. Limitation of Liability</SectionTitle>
          <SectionContent>
            <IntroText>Visibeen is not liable for:</IntroText>
            <BulletPoint>Loss of profile rankings, reviews, or visibility</BulletPoint>
            <BulletPoint>Suspension or penalties by Google</BulletPoint>
            <BulletPoint>Errors in AI-generated or user-generated content</BulletPoint>
            <BulletPoint>Any financial or reputational loss</BulletPoint>
            <BulletPoint>
              You are responsible for all decisions made based on Visibeen recommendations.
            </BulletPoint>
          </SectionContent>

          <SectionTitle>6. Payment Authorization & Mandate Activation</SectionTitle>
          <SectionContent>
            <BulletPoint>
              If you enable auto-deduct, you authorize us to initiate recurring payments through NPCI-approved e-mandate or UPI
              billing systems.
            </BulletPoint>
            <BulletPoint>
              Visibeen uses the mandate activation is required to access services, including Google Business Profile
              integrations.
            </BulletPoint>
            <BulletPoint>
              Mandate activation confirms your consent for automatic deduction or subscription fees on the agreed schedule.
            </BulletPoint>
          </SectionContent>

          <SectionTitle>7. Refunds</SectionTitle>
          <SectionContent>
            <BulletPoint>
              All payments are non-refundable, except if Visibeen fails to provide access to services.
            </BulletPoint>
            <BulletPoint>
              Canceling your billing but does not restart the current cycle.
            </BulletPoint>
          </SectionContent>

          <SectionTitle>8. Indemnity</SectionTitle>
          <SectionContent>
            <IntroText>
              You agree to protect Visibeen from any claims, damages, or losses arising from your use of the platform, including AI
              recommendations and GBP actions.
            </IntroText>
          </SectionContent>

          <SectionTitle>9. Updates</SectionTitle>
          <SectionContent>
            <BulletPoint>
              These terms may be updated as our platform or AI tools evolve.
            </BulletPoint>
            <BulletPoint>
              Continued use of Visibeen implies acceptance of the latest terms.
            </BulletPoint>
          </SectionContent>
        </ContentContainer>
      </ContentSection>

      <Footer />
    </PageContainer>
  );
};

export default TermsOfService;
